import {COUNTRY_CODES} from '@/utils/constants/countryConstants'

export const DEFAULT_LOCALE_LABEL = 'ES-US'

export const LOCALE_COLUMN_LABELS = {
  displayName_locale: 'Display Name',
  displaySize_locale: 'Display Size',
  productDescription_locale: 'Product Description',
  subCategory_locale: 'Subcategory',
}

export const getLocaleHeader = (columnKey, localeLabel) =>
  `${LOCALE_COLUMN_LABELS[columnKey]} (${localeLabel ?? DEFAULT_LOCALE_LABEL})`

const NA_PRODUCT_LIST_COLUMNS = [
  {
    columnKey: 'showOnECommerce',
    header: 'Show on E-Commerce',
    defaultVisible: true,
    editable: true,
    fieldType: 'boolean',
    multiSearch: false,
  },
  {columnKey: 'sorting', header: 'Sorting', defaultVisible: true, editable: true, fieldType: 'text'},
  {
    columnKey: 'displayName_en',
    header: 'Display Name (EN)',
    minSize: 350,
    defaultVisible: true,
    editable: true,
    fieldType: 'text',
    mandatory: true,
  },
  {columnKey: 'productId', header: 'Product ID', defaultVisible: true, mandatory: true},
  {
    columnKey: 'skuNumber',
    header: 'SKU Number',
    minSize: 200,
    defaultVisible: true,
    visibleForCountries: [COUNTRY_CODES.US],
  },
  {
    columnKey: 'upcNumber',
    header: 'UPC Number',
    defaultVisible: true,
    visibleForCountries: [COUNTRY_CODES.US],
  },
  {
    columnKey: 'size',
    header: 'Display Size (EN)',
    defaultVisible: true,
    editable: true,
    fieldType: 'text',
  },
  {
    columnKey: 'displayCategory',
    header: 'Display Category (EN)',
    minSize: 200,
    defaultVisible: true,
    editable: true,
    fieldType: 'text',
    mandatory: true,
  },
  {
    columnKey: 'subCategory_en',
    header: 'Subcategory (EN)',
    minSize: 200,
    defaultVisible: true,
    editable: true,
    fieldType: 'text',
    mandatory: true,
  },
  {
    columnKey: 'displayBrand',
    header: 'Display Brand (EN)',
    minSize: 200,
    defaultVisible: true,
    editable: true,
    fieldType: 'text',
    mandatory: true,
  },
  {
    columnKey: 'multipleSellingUnits',
    header: 'Multiple Selling Units',
    defaultVisible: true,
    editable: true,
    fieldType: 'text',
  },
  {
    columnKey: 'keywords_en',
    header: 'Keywords (EN)',
    minSize: 200,
    defaultVisible: true,
    editable: true,
    fieldType: 'text',
  },
  {
    columnKey: 'minimumOrderQuantity',
    header: 'Minimum Order Quantity',
    defaultVisible: true,
    editable: true,
    fieldType: 'text',
  },
  {
    columnKey: 'newSkuTag',
    header: 'New SKU Tag',
    defaultVisible: true,
    visibleForCountries: [COUNTRY_CODES.US],
    editable: true,
    fieldType: 'boolean',
    multiSearch: false,
  },
  {
    columnKey: 'newSkuStartDate',
    header: 'New SKU Start Date',
    defaultVisible: true,
    visibleForCountries: [COUNTRY_CODES.US],
    editable: true,
    fieldType: 'date',
    filterVariant: 'date',
  },
  {
    columnKey: 'newSkuEndDate',
    header: 'New SKU End Date',
    defaultVisible: true,
    visibleForCountries: [COUNTRY_CODES.US],
    editable: true,
    fieldType: 'date',
    filterVariant: 'date',
  },
  {columnKey: 'gtin', header: 'GTIN', defaultVisible: true, mandatory: true},
  {
    columnKey: 'segmentsToShow',
    header: 'Segments to Show',
    defaultVisible: true,
    editable: true,
    fieldType: 'segment',
  },
  {
    columnKey: 'displayName_locale',
    header: getLocaleHeader('displayName_locale', DEFAULT_LOCALE_LABEL),
    defaultVisible: false,
    editable: true,
    fieldType: 'text',
  },
  {columnKey: 'productName', header: 'Product Name', defaultVisible: false, mandatory: true},
  {
    columnKey: 'displaySize_locale',
    header: getLocaleHeader('displaySize_locale', DEFAULT_LOCALE_LABEL),
    defaultVisible: false,
    editable: true,
    fieldType: 'text',
  },
  {
    columnKey: 'productDescription_locale',
    header: getLocaleHeader('productDescription_locale', DEFAULT_LOCALE_LABEL),
    defaultVisible: false,
    editable: true,
    fieldType: 'text',
  },
  {
    columnKey: 'productDescription_en',
    header: 'Product Description (EN)',
    defaultVisible: false,
    editable: true,
    fieldType: 'text',
  },
  {
    columnKey: 'displayCategoryId',
    header: 'Display Category Id',
    defaultVisible: false,
    editable: true,
    fieldType: 'text',
    mandatory: true,
  },
  {
    columnKey: 'subCategoryId',
    header: 'Subcategory Id',
    defaultVisible: false,
    editable: true,
    fieldType: 'text',
    mandatory: true,
  },
  {columnKey: 'displaySizeId', header: 'Display Size ID', defaultVisible: false, editable: true, fieldType: 'text'},
  {
    columnKey: 'displayBrandId',
    header: 'Display Brand Id',
    defaultVisible: false,
    editable: true,
    fieldType: 'text',
    mandatory: true,
  },
  {columnKey: 'sizeFormatId', header: 'Size/Format Id', defaultVisible: false, editable: true, fieldType: 'text'},
  {columnKey: 'sizeFormat_en', header: 'Size/Format (EN)', defaultVisible: false, editable: true, fieldType: 'text'},
  {columnKey: 'productTypeId', header: 'Product Type Id', defaultVisible: false, editable: true, fieldType: 'text'},
  {columnKey: 'productType_en', header: 'Product Type (EN)', defaultVisible: false, editable: true, fieldType: 'text'},
  {
    columnKey: 'suggestedProductSellingPrice',
    header: 'Suggested Product Selling Price',
    defaultVisible: false,
    editable: true,
    fieldType: 'text',
    multiSearch: false,
  },
]

const PRODUCT_LIST_COLUMNS = [
  {columnKey: 'productId', header: 'Product ID', defaultVisible: true, mandatory: true},
  {
    columnKey: 'status',
    header: 'Status',
    defaultVisible: true,
  },
  {columnKey: 'productName', header: 'Product Name', defaultVisible: true, mandatory: true},
  {columnKey: 'segmentCount', header: 'Segment Count', defaultVisible: true},
  {
    columnKey: 'displayName_en',
    header: 'Display Name (EN)',
    minSize: 350,
    defaultVisible: true,
    editable: true,
    fieldType: 'text',
    mandatory: true,
  },
  {columnKey: 'subCategoryCount', header: 'Subcategory Count', defaultVisible: true},
  {columnKey: 'sorting', header: 'Sorting', defaultVisible: true, editable: true, fieldType: 'text'},
  {
    columnKey: 'showOnECommerce',
    header: 'Show on E-Commerce',
    defaultVisible: true,
    editable: true,
    fieldType: 'boolean',
    multiSearch: false,
  },
  {
    columnKey: 'suggestedProductSellingPrice',
    header: 'Suggested Product Selling Price',
    defaultVisible: true,
    editable: true,
    fieldType: 'text',
    multiSearch: false,
  },
  {
    columnKey: 'multipleSellingUnits',
    header: 'Multiple Selling Units',
    defaultVisible: true,
    editable: true,
    fieldType: 'text',
  },
  {
    columnKey: 'size',
    header: 'Display Size (EN)',
    defaultVisible: false,
    editable: true,
    fieldType: 'text',
    mandatory: true,
  },
  {
    columnKey: 'displayCategory',
    header: 'Display Category (EN)',
    minSize: 200,
    defaultVisible: false,
    editable: true,
    fieldType: 'text',
    mandatory: true,
  },
  {
    columnKey: 'subCategory_en',
    header: 'Subcategory (EN)',
    minSize: 200,
    defaultVisible: false,
    editable: true,
    fieldType: 'text',
    mandatory: true,
  },
  {
    columnKey: 'displayBrand',
    header: 'Display Brand (EN)',
    minSize: 200,
    defaultVisible: false,
    editable: true,
    fieldType: 'text',
    mandatory: true,
  },
  {
    columnKey: 'keywords_en',
    header: 'Keywords (EN)',
    minSize: 200,
    defaultVisible: false,
    editable: true,
    fieldType: 'text',
  },
  {
    columnKey: 'minimumOrderQuantity',
    header: 'Minimum Order Quantity',
    defaultVisible: false,
    editable: true,
    fieldType: 'text',
  },
  {
    columnKey: 'newSkuTag',
    header: 'New SKU Tag',
    defaultVisible: false,
    visibleForCountries: [COUNTRY_CODES.US],
    editable: true,
    fieldType: 'boolean',
    multiSearch: false,
  },
  {columnKey: 'gtin', header: 'GTIN', defaultVisible: false, mandatory: true},
  {
    columnKey: 'segmentsToShow',
    header: 'Segments to Show',
    defaultVisible: false,
    editable: true,
    fieldType: 'segment',
  },
  {
    columnKey: 'displayName_locale',
    header: getLocaleHeader('displayName_locale', DEFAULT_LOCALE_LABEL),
    defaultVisible: false,
    editable: true,
    fieldType: 'text',
  },
  {
    columnKey: 'displaySize_locale',
    header: getLocaleHeader('displaySize_locale', DEFAULT_LOCALE_LABEL),
    defaultVisible: false,
    editable: true,
    fieldType: 'text',
  },
  {
    columnKey: 'productDescription_locale',
    header: getLocaleHeader('productDescription_locale', DEFAULT_LOCALE_LABEL),
    defaultVisible: false,
    editable: true,
    fieldType: 'text',
  },
  {
    columnKey: 'subCategory_locale',
    header: getLocaleHeader('subCategory_locale', DEFAULT_LOCALE_LABEL),
    defaultVisible: false,
    editable: true,
    fieldType: 'text',
  },
  {
    columnKey: 'productDescription_en',
    header: 'Product Description (EN)',
    defaultVisible: false,
    editable: true,
    fieldType: 'text',
  },
  {
    columnKey: 'displayCategoryId',
    header: 'Display Category Id',
    defaultVisible: false,
    editable: true,
    fieldType: 'text',
    mandatory: true,
  },
  {
    columnKey: 'subCategoryId',
    header: 'Subcategory Id',
    defaultVisible: false,
    editable: true,
    fieldType: 'text',
    mandatory: true,
  },
  {columnKey: 'displaySizeId', header: 'Display Size ID', defaultVisible: false, editable: true, fieldType: 'text'},
  {
    columnKey: 'displayBrandId',
    header: 'Display Brand Id',
    defaultVisible: false,
    editable: true,
    fieldType: 'text',
    mandatory: true,
  },
  {columnKey: 'sizeFormatId', header: 'Size/Format Id', defaultVisible: false, editable: true, fieldType: 'text'},
  {columnKey: 'sizeFormat_en', header: 'Size/Format (EN)', defaultVisible: false, editable: true, fieldType: 'text'},
  {columnKey: 'productTypeId', header: 'Product Type Id', defaultVisible: false, editable: true, fieldType: 'text'},
  {columnKey: 'productType_en', header: 'Product Type (EN)', defaultVisible: false, editable: true, fieldType: 'text'},
  {columnKey: 'salesOrg', header: 'Sales Org', defaultVisible: false},
  {columnKey: 'brand', header: 'Brand', defaultVisible: false},
  {columnKey: 'category', header: 'Category', defaultVisible: false},
  {columnKey: 'isCombo', header: 'Is Combo', defaultVisible: false},
  {columnKey: 'routeTypeId', header: 'Route Type ID', defaultVisible: false},
  {columnKey: 'imageAvailability', header: 'Image Availability', defaultVisible: false},
]

export const getProductListColumnsForCountryCode = (countryCode = 'US') => {
  switch (countryCode) {
    case 'US':
      return NA_PRODUCT_LIST_COLUMNS
    default:
      return PRODUCT_LIST_COLUMNS
  }
}
